'use client';
import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Play, ChevronDown, Mic2, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';

const heroSlides = [
  {
    id: 'slide-001',
    image: "/assets/images/_MG_6588-xcWseAPz_dcd4w-HVOqzPw-1786742116231.jpg",
    alt: 'Hadatha, msanii wa Afro Contemporary kutoka Tanzania, picha halisi ya msanii',
    headline: 'The White Lioness',
    subHeadline: 'New Single: Basi feat. Ronze — Out Now',
    cta1: { label: 'Listen Now', href: '/music' },
    cta2: { label: 'Watch Video', href: '/videos' },
  },
  {
    id: 'slide-002',
    image: "https://images.unsplash.com/photo-1701491332798-35f53bc3e9c0",
    alt: 'Hadatha in studio recording session, warm amber lighting, professional microphone setup',
    headline: 'Afro Contemporary',
    subHeadLine: 'From the Southern Highlands of Tanzania to the World',
    subHeadline: 'From the Southern Highlands of Tanzania to the World',
    cta1: { label: 'Explore Music', href: '/music' },
    cta2: { label: 'Book Artist', href: '/booking' },
  },
  {
    id: 'slide-003',
    image: "https://images.unsplash.com/photo-1575487647308-da2b101ff892",
    alt: 'Hadatha live concert performance, large festival stage, energetic crowd, stage lighting effects',
    headline: 'Live Performances',
    subHeadline: 'Available for Concerts, Festivals & Private Events',
    cta1: { label: 'View Events', href: '/events' },
    cta2: { label: 'Enquire Now', href: '/booking' },
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      goNext();
    }, 6000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const goNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent((prev) => (prev + 1) % heroSlides?.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goPrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent((prev) => (prev - 1 + heroSlides?.length) % heroSlides?.length);
      setIsTransitioning(false);
    }, 300);
  };

  const slide = heroSlides?.[current];

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden">
      {/* Background Image */}
      <div className={`absolute inset-0 transition-opacity duration-700 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <AppImage
          src={slide?.image}
          alt={slide?.alt}
          fill
          priority
          className="object-cover object-center"
          sizes="100vw"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>
      {/* Slide indicators */}
      <div className="absolute top-1/2 right-6 -translate-y-1/2 flex flex-col gap-2 z-10">
        {heroSlides?.map((s, i) => (
          <button
            key={s?.id}
            onClick={() => setCurrent(i)}
            className={`w-1 rounded-full transition-all duration-300 ${i === current ? 'h-8 bg-primary' : 'h-4 bg-white/30 hover:bg-white/60'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
      {/* Navigation arrows */}
      <button
        onClick={goPrev}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full glass-card text-white/70 hover:text-white hover:border-primary/40 transition-all duration-150 active:scale-95"
        aria-label="Previous slide"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={goNext}
        className="absolute right-14 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full glass-card text-white/70 hover:text-white hover:border-primary/40 transition-all duration-150 active:scale-95"
        aria-label="Next slide"
      >
        <ChevronRight size={20} />
      </button>
      {/* Content */}
      <div className={`relative z-10 w-full max-w-screen-2xl mx-auto px-6 lg:px-10 pb-28 transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
        {/* Origin badge */}
        <div className="flex items-center gap-2 mb-4">
          <span className="px-3 py-1 text-[11px] font-600 tracking-widest uppercase rounded-full bg-primary/20 text-primary border border-primary/30">
            🇹🇿 Tanzania · Afro Contemporary
          </span>
        </div>

        <h1 className="text-5xl sm:text-7xl lg:text-8xl font-800 text-foreground hero-text-shadow mb-3 leading-none tracking-tight">
          {slide?.headline}
        </h1>
        <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl font-400">
          {slide?.subHeadline}
        </p>

        <div className="flex flex-wrap items-center gap-4">
          <Link
            href={slide?.cta1?.href}
            className="flex items-center gap-2.5 px-6 py-3.5 rounded-xl bg-gradient-gold text-primary-foreground text-sm font-700 hover:opacity-90 active:scale-95 transition-all duration-150 gold-glow"
          >
            <Play size={16} fill="currentColor" />
            {slide?.cta1?.label}
          </Link>
          <Link
            href={slide?.cta2?.href}
            className="flex items-center gap-2.5 px-6 py-3.5 rounded-xl glass-card text-white text-sm font-600 hover:border-primary/40 active:scale-95 transition-all duration-150"
          >
            <ExternalLink size={16} />
            {slide?.cta2?.label}
          </Link>
          <Link
            href="/booking"
            className="flex items-center gap-2.5 px-6 py-3.5 rounded-xl border border-white/20 text-white/80 text-sm font-600 hover:border-primary/40 hover:text-white active:scale-95 transition-all duration-150"
          >
            <Mic2 size={16} />
            Book Artist
          </Link>
        </div>

        {/* Latest release pill */}
        <div className="mt-8 inline-flex items-center gap-3 px-4 py-2 rounded-full glass-card">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5]?.map((b) => (
              <div
                key={`wave-${b}`}
                className="waveform-bar w-1"
                style={{ height: `${8 + b % 3 * 6}px`, animationDelay: `${b * 0.12}s` }}
              />
            ))}
          </div>
          <span className="text-xs text-white/70 font-500">Now Streaming</span>
          <span className="text-xs font-700 text-primary">Basi feat. Ronze</span>
          <span className="text-[10px] text-white/40">2026</span>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-[10px] tracking-widest text-white/40 uppercase">Scroll</span>
        <ChevronDown size={16} className="text-white/40" />
      </div>
    </section>
  );
}